import React, { memo } from "react";

/**
 * Vertical A-Z (+ '#' for numbers) jump-list, contacts-app style.
 *
 * Sits sticky to the left of the realtors table on desktop only.
 * Clicking a letter fires `onJump(realtorId)` for the first realtor
 * in the current sorted list whose name starts with that letter.
 * Letters with no matches are greyed out and disabled.
 */
function AlphabetRolodex({ letters, firstIdByLetter, onJump, className = "" }) {
  return (
    <nav
      aria-label="Alphabet index"
      data-testid="alphabet-rolodex"
      className={`sticky top-24 flex flex-col items-center justify-center gap-0.5 select-none w-6 shrink-0 text-[11px] font-medium tracking-wide max-h-[calc(100vh-8rem)] overflow-y-auto ${className}`}
    >
      {letters.map((ch) => {
        const id = firstIdByLetter[ch];
        const enabled = !!id;
        const label = ch === "#" ? "#" : ch;
        return (
          <button
            key={ch}
            type="button"
            disabled={!enabled}
            onClick={() => enabled && onJump(id, ch)}
            data-testid={`rolodex-letter-${ch === "#" ? "hash" : ch}`}
            aria-label={enabled ? `Jump to ${label}` : `${label} — no realtors`}
            title={enabled ? `Jump to ${label}` : ""}
            className={
              enabled
                ? "leading-none py-0.5 w-full text-center text-slate-500 hover:text-[hsl(var(--brand-strong))] hover:bg-slate-100 rounded transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand))]"
                : "leading-none py-0.5 w-full text-center text-slate-300 cursor-default"
            }
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

// Memoise — parent re-renders every JobsContext tick (~1.5s during
// enrich_all). The letter set + firstIdByLetter map only changes when
// the realtors list is re-sorted or realtors are added/removed, so this
// component almost never needs to re-render during a hot enrichment run.
export default memo(AlphabetRolodex, (prev, next) => {
  if (prev.letters !== next.letters) return false;
  if (prev.firstIdByLetter !== next.firstIdByLetter) return false;
  if (prev.onJump !== next.onJump) return false;
  if (prev.className !== next.className) return false;
  return true;
});
